import React from "react";
import { Form, Row, Col, Badge, InputGroup, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaUndo, FaPlus, FaTimes, FaQuestionCircle } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import { ChatbotSettings } from "../types";
import VideoHelpModal from "../../../../../components/common/VideoHelpModal";

interface ProfileSettingsProps {
  settingsData: ChatbotSettings;
  setSettingsData: React.Dispatch<React.SetStateAction<ChatbotSettings>>;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  settingsData,
  setSettingsData,
}) => {
  const { t } = useTranslation();
  const [newTopic, setNewTopic] = React.useState("");
  const [showVideoModal, setShowVideoModal] = React.useState(false);



  const tonePresets = [
    "friendly",
    "professional",
    "casual",
    "concise",
    "empathetic",
  ];

  const bannedSuggestionsByLang: Record<string, string[]> = {
    en: [
      "Personal Data",
      "Religion",
      "Racism",
      "Profanity",
      "Hate Speech",
      "Sexual Content",
      "Violence",
      "Medical Advice",
      "Legal Advice",
      "Financial Advice",
    ],
    tr: [
      "Kişisel Veri",
      "Din",
      "Irkçılık",
      "Argo",
      "Nefret Söylemi",
      "Cinsel İçerik",
      "Şiddet",
      "Tıbbi Tavsiye",
      "Hukuki Tavsiye",
      "Finansal Tavsiye",
    ],
  };

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "tr", name: "Türkçe", flag: "🇹🇷" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "ru", name: "Русский", flag: "🇷🇺" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "ja", name: "日本語", flag: "🇯🇵" },
    { code: "ko", name: "한국어", flag: "🇰🇷" },
    { code: "zh", name: "中文", flag: "🇨🇳" },
  ];

  const handleAddBannedTopic = () => {
    if (!newTopic.trim()) return;
    const currentList = settingsData.guardrails?.banned_topics || [];
    if (!currentList.includes(newTopic.trim())) {
      setSettingsData({
        ...settingsData,
        guardrails: {
          ...settingsData.guardrails,
          banned_topics: [...currentList, newTopic.trim()],
        },
      });
    }
    setNewTopic("");
  };

  const handleRemoveBannedTopic = (topic: string) => {
    const currentList = settingsData.guardrails?.banned_topics || [];
    setSettingsData({
      ...settingsData,
      guardrails: {
        ...settingsData.guardrails,
        banned_topics: currentList.filter((t) => t !== topic),
      },
    });
  };

  return (
    <div className="profile-tabs-card">
      <div className="premium-helper-banner d-flex justify-content-between align-items-center mb-4 p-3 rounded-3 border-start border-4 border-info shadow-sm bg-light bg-opacity-50">
        <div className="d-flex align-items-center">
          <div className="icon-badge me-3 bg-info bg-opacity-10 p-2 rounded-circle d-flex align-items-center justify-content-center">
            <Icon icon={FaQuestionCircle} className="text-info" size="1.25rem" />
          </div>
          <div>
            <h6 className="mb-0 fw-bold text-dark">{t("chatbots.training_guide", "Training Guide")}</h6>
          </div>
        </div>
        <Button
          variant="info"
          size="sm"
          className="rounded-pill px-4 fw-600 text-white shadow-sm hover-elevate"
          onClick={() => setShowVideoModal(true)}
        >
          <Icon icon={FaQuestionCircle} className="me-2" />
          {t("chatbots.watch_tutorial", "Watch Tutorial")}
        </Button>
      </div>

      <Form.Group className="mb-2">
        <Form.Label>{t("chatbots_edit.persona")}</Form.Label>
        <Form.Control
          as="textarea"
          id="persona_input"
          name="persona"
          rows={4}
          value={settingsData.assistant_profile?.persona ?? ""}
          onChange={(e) => {
            setSettingsData({
              ...settingsData,
              assistant_profile: {
                ...(settingsData.assistant_profile || {}),
                persona: e.target.value,
              },
            });
          }}
          placeholder={t("chatbots_edit.persona_placeholder")}
        />
      </Form.Group>

      <Row>
        <Col>
          <Form.Group className="mb-2">
            <Form.Label>{t("chatbots_edit.tone")}</Form.Label>
            {(() => {
              const currentTone: string =
                settingsData.assistant_profile?.tone || "friendly";
              const toneSelectVal = tonePresets.includes(currentTone)
                ? currentTone
                : "custom";
              return (
                <>
                  <Form.Select
                    id="tone_select"
                    name="tone"
                    value={toneSelectVal}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "custom") {
                        // keep current custom tone
                      } else {
                        setSettingsData({
                          ...settingsData,
                          assistant_profile: {
                            ...(settingsData.assistant_profile || {}),
                            tone: val,
                          },
                        });
                      }
                    }}
                  >
                    {tonePresets.map((toneKey) => (
                      <option key={toneKey} value={toneKey}>
                        {t(`chatbots_edit.tone_${toneKey}`)}
                      </option>
                    ))}
                    <option value="custom">{t("chatbots_edit.tone_custom")}</option>
                  </Form.Select>
                  {toneSelectVal === "custom" && (
                    <Form.Control
                      type="text"
                      className="mt-2"
                      value={currentTone}
                      onChange={(e) =>
                        setSettingsData({
                          ...settingsData,
                          assistant_profile: {
                            ...(settingsData.assistant_profile || {}),
                            tone: e.target.value,
                          },
                        })
                      }
                      placeholder={t("chatbots_edit.tone_custom_placeholder")}
                    />
                  )}
                </>
              );
            })()}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-2">
            <Form.Label>{t("chatbots_edit.default_language")}</Form.Label>
            <Form.Select
              id="language_select"
              name="default_language"
              value={settingsData.assistant_profile?.default_language || "en"}
              onChange={(e) => {
                setSettingsData({
                  ...settingsData,
                  assistant_profile: {
                    ...(settingsData.assistant_profile || {}),
                    default_language: e.target.value,
                  },
                });
              }}
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mt-3">
        <Form.Label>{t("chatbots_edit.banned_topics")}</Form.Label>
        <div className="mb-2 d-flex flex-wrap gap-2">
          {(settingsData.guardrails?.banned_topics || []).map((topic) => (
            <Badge
              key={topic}
              bg="danger"
              className="p-2 d-flex align-items-center gap-2"
            >
              {topic}
              <span
                style={{ cursor: "pointer", marginLeft: "4px", display: "inline-flex" }}
                onClick={() => handleRemoveBannedTopic(topic)}
              >
                <Icon icon={FaTimes} />
              </span>
            </Badge>
          ))}
          {(!settingsData.guardrails?.banned_topics ||
            settingsData.guardrails.banned_topics.length === 0) && (
              <span className="text-muted small">{t("chatbots_edit.no_banned_topics")}</span>
            )}
        </div>

        <div className="add-topic-section">
          <InputGroup size="sm" id="banned_topics_section">
            <Form.Control
              id="add_topic_input"
              placeholder={t("chatbots_edit.add_topic_placeholder")}
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddBannedTopic();
                }
              }}
            />
            <Button
              id="add_topic_btn"
              variant="outline-secondary"
              onClick={handleAddBannedTopic}
            >
              <Icon icon={FaPlus} /> {t("chatbots_edit.add")}
            </Button>
          </InputGroup>
        </div>

        <div className="mt-2">
          <small className="text-muted d-block mb-1">{t("chatbots_edit.suggested")}</small>
          <div className="d-flex flex-wrap gap-1">
            {(
              bannedSuggestionsByLang[
              settingsData.assistant_profile?.default_language || "en"
              ] || bannedSuggestionsByLang["en"]
            ).filter(s => !(settingsData.guardrails?.banned_topics || []).includes(s)).map((s) => (
              <Badge
                key={s}
                bg="light"
                text="dark"
                className="border cursor-pointer hover-bg-gray"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const current =
                    settingsData.guardrails?.banned_topics || [];
                  if (!current.includes(s)) {
                    setSettingsData({
                      ...settingsData,
                      guardrails: {
                        ...settingsData.guardrails,
                        banned_topics: [...current, s],
                      },
                    });
                  }
                }}
              >
                + {s}
              </Badge>
            ))}
          </div>
        </div>

        <div className="mt-3 d-flex justify-content-end">
          <Button
            variant="outline-danger"
            size="sm"
            className="reset-topics-btn"
            onClick={() => {
              setSettingsData({
                ...settingsData,
                guardrails: {
                  ...settingsData.guardrails,
                  banned_topics: [],
                },
              });
            }}
          >
            <Icon icon={FaUndo} className="me-1" /> {t("chatbots_edit.reset_guardrails")}
          </Button>
        </div>
      </Form.Group>

      <div className="training-note">
        <small>
          <Icon icon={FaUndo} className="me-2 text-primary" />
          {t("chatbots_edit.persona_note")}
        </small>
      </div>

      <VideoHelpModal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        videoUrl="/training/persona_guardrails.mp4"
        title={t("chatbots.tutorial_persona", "Tutorial: Assistant Profile & Guardrails")}
      />
    </div>
  );
};

export default ProfileSettings;

